"use client";

import type { JSX } from "react";
import { Button, Icon, Text, View } from "@/components";
import { useLayout } from "@/providers";
import cn from "@/utils/cn";

export interface SidebarToggleProps {
  /** Clases extra para ajustar la posición desde la página o el layout. */
  className?: string;
}

/**
 * @name SidebarToggle
 * @description Abre y cierra el sidebar del backoffice por debajo de 1400px
 * (el umbral que usa `app/backoffice/layout.tsx` para dejarlo fijo).
 *
 * El `SidebarVertical` del template ya es un drawer completo (entra desde la
 * izquierda, se cierra al tocar afuera o al elegir un ítem) pero arranca cerrado
 * bajo ese ancho, y el único disparador que traía el template vive en el `Nav`
 * de `ansv_lib` dentro de la rama `userAuth`, que sale de `sessionStorage` y
 * este proyecto nunca setea (la sesión va por cookie). Sin este botón no hay
 * navegación ni logout —ambos viven en el sidebar— en celulares, tablets ni
 * notebooks de 1366px.
 *
 * El `id="sidebar-toggle"` no es decorativo: el handler de "click afuera" del
 * `SidebarVertical` lo busca por id para no cerrar el drawer con el mismo click
 * que lo abre.
 *
 * @param {SidebarToggleProps} props - Clases extra opcionales.
 * @returns {JSX.Element} El botón de menú.
 */
const SidebarToggle = ({ className }: SidebarToggleProps): JSX.Element => {
  const { t, sidebarIsOpen, setSidebarIsOpen } = useLayout();

  return (
    <View className={cn("w-fit min-[1400px]:hidden", className)}>
      <Button
        id="sidebar-toggle"
        action={() => setSidebarIsOpen?.(!sidebarIsOpen)}
        label={t(sidebarIsOpen ? "closeMenu" : "openMenu")}
        aria-expanded={!!sidebarIsOpen}
        // El alto sale del padding y no de min-h-*: el template declara
        // `button { min-h-10 }` fuera de @layer base y el CSS sin capa gana
        // sobre las utilities de Tailwind.
        className="inline-flex items-center px-4 py-3 sm:py-2.5 rounded-full border border-border bg-card text-sm font-medium text-foreground hover:bg-muted transition-colors"
        // El átomo Button envuelve los children en un Text: la separación entre
        // el ícono y la etiqueta va en ese wrapper, no en el <button>.
        styleText="inline-flex items-center gap-2"
      >
        <Icon name={sidebarIsOpen ? "xmark" : "bars"} size="4x" />
        <Text type="span">{t("menu")}</Text>
      </Button>
    </View>
  );
};

export default SidebarToggle;
